import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { clients } from '../data/mock';
import { caseStudies } from '../data/caseStudies';
import { playBrandStinger, stopBrandStinger } from '../lib/brandSounds';

const BRAND_LOGOS = {
  nike: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/6708jfdd_Nike-Logo.png',
  comisario: 'https://customer-assets.emergentagent.com/job_cinema-hub-693/artifacts/bhqjatih_client-comisario-removebg-preview.png',
};

function BrandLogo({ style, name }) {
  // Stylized typographic logos in black on cream
  const common = 'flex items-center justify-center w-full h-full';

  // Real brand assets (user-supplied)
  if (BRAND_LOGOS[style]) {
    return (
      <div className={common + ' p-4'}>
        <img
          src={BRAND_LOGOS[style]}
          alt={name}
          className="max-h-[60%] max-w-[70%] object-contain"
          draggable={false}
        />
      </div>
    );
  }

  switch (style) {
    case 'nike':
      return (
        <div className={common}>
          <svg viewBox="0 0 200 80" className="w-2/3 max-w-[220px]">
            <text x="50%" y="55%" textAnchor="middle" fontFamily="Impact, sans-serif" fontSize="56" fontStyle="italic" fontWeight="900" fill="#000">NIKE</text>
            <path d="M30 55 C70 30 130 25 180 35 L170 48 C130 42 80 48 40 60 Z" fill="#000" />
          </svg>
        </div>
      );
    case 'mercedes':
      return (
        <div className={common}>
          <svg viewBox="0 0 220 50" className="w-2/3 max-w-[260px]">
            <circle cx="25" cy="25" r="16" fill="none" stroke="#000" strokeWidth="2" />
            <path d="M25 9 L25 25 M25 25 L12 33 M25 25 L38 33" stroke="#000" strokeWidth="2" />
            <text x="55" y="32" fontFamily="Georgia, serif" fontSize="22" fill="#000">Mercedes-Benz</text>
          </svg>
        </div>
      );
    case 'comisario':
      return (
        <div className={common + ' flex-col text-black'}>
          <p className="font-serif italic text-[13px] tracking-[0.3em]">TEQUILA</p>
          <p className="font-serif text-3xl md:text-5xl tracking-tight">Comisario</p>
          <p className="font-serif italic text-[10px] tracking-[0.3em] mt-1">100% PURO DE AGAVE</p>
        </div>
      );
    case 'jones':
      return (
        <div className={common + ' flex-col text-black'}>
          <p className="font-display text-3xl md:text-4xl tracking-[0.15em]">JONES</p>
          <p className="font-display text-xs tracking-[0.4em] mt-1">SODA CO.</p>
        </div>
      );
    case 'delta':
      return (
        <div className={common}>
          <svg viewBox="0 0 220 60" className="w-2/3 max-w-[260px]">
            <polygon points="10,50 35,12 60,50" fill="#000" />
            <polygon points="22,50 35,30 48,50" fill="#fff" />
            <text x="75" y="42" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="32" letterSpacing="4" fill="#000">DELTA</text>
          </svg>
        </div>
      );
    default:
      return <div className={common + ' text-black font-display text-2xl'}>{name}</div>;
  }
}

export default function Clients() {
  return (
    <section id="work" className="relative px-5 md:px-10 lg:px-14 py-24 md:py-32 border-t border-white/5">
      <div className="flex items-center gap-4 mb-10">
        <span className="w-10 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/70">04 / SELECTED CLIENTS</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <h2 className="font-display text-[8vw] md:text-[5vw] lg:text-[5rem] leading-[0.95] tracking-tight max-w-3xl">
          <span className="text-white">Built with brands</span>
          <br />
          <span className="text-white">that </span>
          <span className="font-script text-white/30">move culture.</span>
        </h2>
        <a
          href="#contact"
          className="group inline-flex items-center gap-2 text-[11px] tracking-[0.3em] text-white/80 hover:text-white border-b border-white/30 hover:border-white pb-1.5 w-fit"
        >
          DISCUSS YOUR BRAND
          <span className="group-hover:translate-x-1 transition-transform">→</span>
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 [perspective:1200px]">
        {clients.map((c, i) => {
          // Custom grid spans for collage effect
          const spans = [
            'md:col-span-7 md:row-span-2 h-[420px] md:h-[520px]',
            'md:col-span-5 h-[260px]',
            'md:col-span-4 h-[260px]',
            'md:col-span-4 h-[260px]',
            'md:col-span-4 h-[260px]',
            'md:col-span-12 h-[300px]',
          ];
          const tiltDir = i % 2 === 0 ? 'group-hover:-rotate-[0.6deg]' : 'group-hover:rotate-[0.6deg]';
          const hasCase = !!caseStudies[c.logoStyle];
          const csEntry = caseStudies[c.logoStyle];
          const isComingSoon = !!csEntry?.comingSoon;
          const sharedProps = {
            'aria-label': hasCase ? `Read ${c.name} case study` : `Visit ${c.name}`,
            onMouseEnter: () => playBrandStinger(c.logoStyle),
            onMouseLeave: () => stopBrandStinger(),
            style: { '--hover-bg': c.hoverBg },
            className: `group relative bg-[#F4EFE4] hover:bg-[var(--hover-bg)] p-8 md:p-10 flex flex-col justify-between overflow-hidden will-change-transform transition-[background-color,transform,box-shadow] duration-500 ease-out hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] ${tiltDir} ${spans[i]}`,
          };
          const tileContent = (
            <>
              {/* Roaming spotlight on hover */}
              <span
                className="pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  background:
                    'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 55%)',
                }}
              />
              {/* Animated sweep bar */}
              <span className="pointer-events-none absolute top-0 left-0 h-[2px] w-0 bg-white/80 group-hover:w-full transition-all duration-700 ease-out" />

              <div className="relative flex items-start justify-between">
                <ArrowUpRight className="w-5 h-5 text-black/60 group-hover:text-white group-hover:-translate-y-2 group-hover:translate-x-2 group-hover:scale-125 transition-all duration-500" />
                <div className="flex items-center gap-2">
                  {isComingSoon ? (
                    <span
                      className="text-[9px] tracking-[0.3em] text-black bg-black/5 group-hover:bg-white/15 group-hover:text-white border border-black/20 group-hover:border-white/30 px-2 py-0.5 transition-colors"
                      data-testid="client-coming-soon-badge"
                    >
                      COMING SOON · {csEntry.comingSoonYear || 'SOON'}
                    </span>
                  ) : (
                    hasCase && (
                      <span className="text-[9px] tracking-[0.3em] text-black/70 group-hover:text-white/85 border border-black/20 group-hover:border-white/30 px-2 py-0.5 transition-colors">
                        CASE STUDY
                      </span>
                    )
                  )}
                  <span className="text-[10px] tracking-[0.3em] text-black/50 group-hover:text-white/80 transition-colors duration-500">
                    {c.num}
                  </span>
                </div>
              </div>

              <div className="relative flex-1 flex items-center justify-center min-h-[120px]">
                <div className={`transition-[filter] duration-500 ease-out group-hover:[filter:invert(1)] motion-${c.motion}`}>
                  <BrandLogo style={c.logoStyle} name={c.name} />
                </div>
              </div>

              <div className="relative flex items-center gap-3 text-[10px] tracking-[0.3em] text-black/60 group-hover:text-white/85 transition-colors duration-500">
                <span className="w-6 group-hover:w-12 h-px bg-[#FF3D2E] group-hover:bg-white transition-all duration-500" />
                {isComingSoon ? `LAUNCHING ${csEntry.comingSoonYear || 'SOON'}` : c.tag}
              </div>
            </>
          );
          return hasCase ? (
            <Link key={c.num} to={`/work/${c.logoStyle}`} {...sharedProps}>
              {tileContent}
            </Link>
          ) : (
            <a key={c.num} href={c.url} target="_blank" rel="noopener noreferrer" {...sharedProps}>
              {tileContent}
            </a>
          );
        })}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
        <p className="text-[11px] tracking-[0.3em] text-white/50">
          …AND A CONFIDENTIAL ROSTER OF FOUNDERS, ATHLETES &amp; TALENT.
        </p>
        <a
          href="#contact"
          className="inline-flex items-center gap-2 px-5 py-3 border border-white/25 text-[11px] tracking-[0.25em] text-white hover:bg-white hover:text-black transition-colors"
        >
          BECOME THE NEXT ONE <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </section>
  );
}
